import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { MailX, CheckCircle, AlertTriangle, Home } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  const email = searchParams.get('email');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!email || !token) {
      setStatus('error');
      setMessage('Invalid unsubscribe link. Please check your email and try again.');
      return;
    }

    handleUnsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, token]);

  const handleUnsubscribe = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/unsubscribe?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`
      );

      if (response.ok) {
        const data = await response.json();
        setStatus('success');
        setMessage(data.message || 'You have been successfully unsubscribed.');
      } else {
        const error = await response.json();
        setStatus('error');
        setMessage(error.detail || 'Failed to unsubscribe. The link may be invalid or expired.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('An error occurred. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 bg-[#f5f5f5] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <MailX className="w-8 h-8 text-[#737373]" />
            </div>
            <h1 className="text-xl font-bold text-[#0d0d0d] mb-2">Processing...</h1>
            <p className="text-[#737373]">Please wait while we process your request.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-[#0d0d0d] mb-2">Unsubscribed</h1>
            <p className="text-[#737373] mb-6">{message}</p>
            <p className="text-sm text-[#737373] mb-6">
              You will no longer receive marketing emails from BusinessMint PR. 
              You will still receive transactional emails related to your orders.
            </p>
            <Link to="/">
              <Button className="bg-[#D4AF37] hover:bg-[#B8960F] text-[#0d0d0d]">
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-[#0d0d0d] mb-2">Oops!</h1>
            <p className="text-[#737373] mb-6">{message}</p>
            <Link to="/">
              <Button className="bg-[#D4AF37] hover:bg-[#B8960F] text-[#0d0d0d]">
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>
            </Link>
          </>
        )}

        <div className="mt-8 pt-6 border-t border-[#e5e5e5]">
          <p className="text-xs text-[#737373]">
            Need help? Contact us at{' '}
            <a href="mailto:support@businessmintpr.com" className="text-[#D4AF37] hover:underline">
              support@businessmintpr.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
